import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export type SimpleComboboxOption = {
  value: string;
  label: string;
};

interface SimpleComboboxProps {
  id?: string;
  placeholder: string;
  searchPlaceholder?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: SimpleComboboxOption[];
  disabled?: boolean;
  error?: string;
}

export function SimpleCombobox({
  id,
  placeholder,
  searchPlaceholder = "Search...",
  value,
  onValueChange,
  options,
  disabled,
  error,
}: SimpleComboboxProps) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => options.find((o) => o.value === value),
    [options, value],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            error && "border-destructive",
          )}
          disabled={disabled}
        >
          <span className="truncate">{selected?.label ?? placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={4}
        avoidCollisions={false}
        className="w-[--radix-popper-anchor-width] p-0 z-50 bg-popover border border-border shadow-lg rounded-lg"
      >
        <Command className="bg-popover">
          <CommandInput placeholder={searchPlaceholder} className="h-10 border-b border-border" />
          <CommandList className="max-h-60">
            <CommandEmpty>No option found.</CommandEmpty>
            <CommandGroup className="p-1">
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={() => {
                    onValueChange(opt.value);
                    setOpen(false);
                  }}
                  className="px-3 py-2 rounded-md cursor-pointer"
                >
                  <span className="flex-1">{opt.label}</span>
                  <Check
                    className={cn(
                      "ml-2 h-4 w-4",
                      value === opt.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
